/**
 * IFRAP-AI.fr - Documentation Page
 * Integrated user guide and system documentation
 */

import React, { useState, useEffect } from 'react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DocumentationPage = () => {
  const [docs, setDocs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeModule, setActiveModule] = useState('core');

  useEffect(() => {
    const fetchDocs = async () => {
      try {
        const token = localStorage.getItem('ifrap_token');
        const response = await fetch(`${API}/system/documentation`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        
        if (response.ok) {
          const data = await response.json();
          setDocs(data);
        }
      } catch (err) {
        console.error('Error fetching docs:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDocs();
  }, [API]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-[#FFB000] font-mono animate-pulse">Chargement...</div>
      </div>
    );
  }

  const statusColors = {
    active: 'bg-green-500/20 text-green-400 border-green-500/30',
    beta: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    development: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    planned: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-[#FFB000] font-['Space_Grotesk']">
          Documentation
        </h1>
        <p className="text-gray-400 mt-1">
          Guide d'utilisation et référence système
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Module Navigation */}
        <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg p-4">
          <h2 className="text-white font-medium mb-4">Modules</h2>
          <div className="space-y-1">
            {docs?.modules && Object.entries(docs.modules).map(([key, module]) => (
              <button
                key={key}
                onClick={() => setActiveModule(key)}
                className={`w-full text-left px-3 py-2 rounded transition-colors ${
                  activeModule === key 
                    ? 'bg-[#FFB000]/20 text-[#FFB000]' 
                    : 'text-gray-400 hover:bg-[#1a1a1a]'
                }`}
              >
                <div className="font-medium">{module.name}</div>
                {module.status === 'planned' && (
                  <span className="text-xs text-gray-500">À venir</span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Module Content */}
        <div className="lg:col-span-3 space-y-6">
          {/* Module Info */}
          {docs?.modules?.[activeModule] && (
            <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg p-6">
              <h2 className="text-xl font-bold text-white mb-2">
                {docs.modules[activeModule].name}
              </h2>
              <p className="text-gray-400 mb-4">
                {docs.modules[activeModule].description}
              </p>
              
              {/* Features list */}
              <h3 className="text-white font-medium mb-3">Fonctionnalités</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {docs.modules[activeModule].features.map((featureId) => {
                  const feature = docs.features[featureId];
                  if (!feature) return null;
                  
                  return (
                    <div 
                      key={featureId}
                      className="bg-[#050505] border border-[#2a2a2a] rounded-lg p-4"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-white font-medium">{feature.name}</span>
                        <span className={`px-2 py-0.5 text-xs rounded border ${statusColors[feature.status]}`}>
                          {feature.status === 'active' ? 'Actif' : 
                           feature.status === 'beta' ? 'Beta' :
                           feature.status === 'development' ? 'Dev' : 'Planifié'}
                        </span>
                      </div>
                      <p className="text-gray-500 text-sm">{feature.description}</p>
                      <div className="mt-2 text-xs text-gray-600">
                        Ajouté en v{feature.version_added}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Quick Start */}
          <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg p-6">
            <h2 className="text-xl font-bold text-white mb-4">Guide de démarrage rapide</h2>
            <div className="space-y-4">
              {docs?.quick_start?.map((step, idx) => (
                <div key={idx} className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-[#FFB000] rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-[#050505] font-bold">{step.step}</span>
                  </div>
                  <div>
                    <h3 className="text-white font-medium">{step.title}</h3>
                    <p className="text-gray-400 text-sm">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Keyboard Shortcuts */}
          <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg p-6">
            <h2 className="text-xl font-bold text-white mb-4">Raccourcis clavier</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Ouvrir la recherche</span>
                <kbd className="px-2 py-1 bg-[#050505] border border-[#2a2a2a] rounded text-white text-sm font-mono">Ctrl + K</kbd>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Console Verbose</span>
                <kbd className="px-2 py-1 bg-[#050505] border border-[#2a2a2a] rounded text-white text-sm font-mono">Ctrl + `</kbd>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Dashboard</span>
                <kbd className="px-2 py-1 bg-[#050505] border border-[#2a2a2a] rounded text-white text-sm font-mono">Ctrl + D</kbd>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Aide</span>
                <kbd className="px-2 py-1 bg-[#050505] border border-[#2a2a2a] rounded text-white text-sm font-mono">?</kbd>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentationPage;
